import { isRejected, Middleware } from '@reduxjs/toolkit';
import { pushNotification } from './notificationsSlice';
import { i18n } from 'utils/i18n';

/**
 * Thunks que gestionan sus propios errores y no deben generar un toast automático.
 * Añadir aquí cualquier thunk de polling o con manejo de error propio.
 */
const SILENT_ACTION_PREFIXES = [
  'alarmQueue/getAlarmQueue',
  // Grabación de la llamada seleccionada en el expediente: sin grabación (llamada no
  // cursada) el reproductor simplemente no se pinta; un toast por cada selección sería ruido.
  'alarms/getAlarmRecordingUrl',
  'alarmQueue/getAlarmQueueLastUpdate',
  // Cola de expedientes (F5): se refresca a rebufo del polling clásico; su fallo
  // degrada a lista vacía y el siguiente ciclo reintenta.
  'expedientQueue/getExpedientQueue',
  // Adopción automática de las alertas nuevas de mis expedientes: corre sola a rebufo del
  // poll y se reintenta; un toast sin contexto solo desconcertaría al operador.
  'expedientQueue/adoptMyExpedientAlerts',
  // Nota IA de la llamada en curso: sondeo cada 3 s mientras hay llamada; sin nota (404 lógico)
  // o con el servicio caído, el panel simplemente no se pinta.
  'aiNotes/getAiNoteLive',
  // Notas IA del expediente: se repiden con cada poll de la atención y cada 10 s mientras alguna
  // siga en directo; si fallan, la sección se queda como estaba y el siguiente ciclo reintenta.
  'aiNotes/getExpedientNotes',
  // Estado vivo del expediente en atención: se repide con cada poll; si falla, el timeline
  // sigue con la última foto y el siguiente ciclo reintenta.
  'expedientQueue/getExpedientLiveAlerts',
  // Reanudar la atención tras F5: si falla, el expediente sigue en «Mis expedientes» o en la
  // cola; el handle interior ya avisa si otro operador se lo llevó.
  'expedientQueue/resumeExpedientAttention',
  // Dejar en espera: el panel avisa con un mensaje propio (y si el SAC ya lo soltó, pide repetirlo).
  'expedientQueue/holdExpedient',
  'systemStatus/',
  // Informativo (iconos/mensaje de canal TSV): si falla, la UI degrada a no mostrar nada
  // y se reintenta en el siguiente montaje — un toast por fallo sería solo ruido.
  'measures/getPatientTsvMeasureTypes',
  // Facturación: cada pantalla traduce el errorCode (600X) a un mensaje concreto (ver features/billing/errors.ts).
  'billing/',
  // Novedades: el contador se pide en segundo plano; si falla, el menú se queda sin indicador y reintenta luego.
  'releaseNotes/',
  // Seguir expedientes: el resumen se repide cada minuto, el estado del botón y el «visto» al abrir un
  // expediente van en segundo plano. Seguir / dejar de seguir sí avisan (los pulsa el usuario).
  'expedientFollow/getSummary',
  'expedientFollow/getState',
  'expedientFollow/markSeen',
];

/**
 * Middleware que muestra un toast de error cuando cualquier thunk es rechazado,
 * excepto los listados en SILENT_ACTION_PREFIXES.
 */
export const errorNotificationMiddleware: Middleware = (store) => (next) => (action) => {
  const result = next(action);

  if (isRejected(action)) {
    // Un thunk abortado (petición sustituida o componente desmontado) no es un
    // error del backend: no debe generar toast.
    if ((action.meta as any)?.aborted) return result;
    const isSilent = SILENT_ACTION_PREFIXES.some((prefix) => action.type.startsWith(prefix));
    if (!isSilent) {
      store.dispatch(pushNotification({ message: i18n('error.api.generic'), severity: 'error' }));
    }
  }

  return result;
};
